import { Link } from '@tanstack/react-router';
import type { Artifact } from '@/api/schemas';
import { useArtifactInstall, type ArtifactInstall } from '@/api/endpointsInstall';
import { CopyButton } from '@/components/CopyButton';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { QrCode } from '@/components/QrCode';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useNow } from '@/lib/useNow';
import { expiryText, installSteps, installTitle, minutesLeft, pointsToDevices } from './installModel';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  artifact: Artifact | null;
}

/**
 * Install on a phone (SPEC decision 28): a QR code and a link to the build's install page, valid 15 minutes, generated in
 * the browser (no QR service sees the link). The phone opens the page without signing in; the API decides what installs
 * and says why when it does not (an AAB, a Simulator or App Store build).
 */
export function InstallSheet({ open, onOpenChange, artifact }: Props) {
  const query = useArtifactInstall(artifact?.id, open);
  const info = query.data;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="install-sheet" className="w-full gap-0 p-0 sm:max-w-md" aria-describedby="install-sheet-desc">
        <SheetHeader>
          <SheetTitle>Install on a phone</SheetTitle>
          <SheetDescription id="install-sheet-desc" className="truncate text-xs">
            {info ? installTitle(info) : (artifact?.filename ?? 'Internal build')}
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-5">
          {query.isLoading && !info ? <InstallSkeleton /> : null}
          {query.isError && !info ? <ErrorState compact error={query.error} onRetry={() => void query.refetch()} /> : null}
          {info && !info.installable ? <NotInstallable info={info} /> : null}
          {info?.installable && info.installPageUrl ? <InstallReady info={info} url={info.installPageUrl} refreshing={query.isFetching} onNewLink={() => void query.refetch()} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InstallReady({ info, url, refreshing, onNewLink }: { info: ArtifactInstall; url: string; refreshing: boolean; onNewLink: () => void }) {
  const now = useNow(15_000);
  const expired = minutesLeft(info.expiresAt, now) <= 0;
  return (
    <>
      <section className="enter flex flex-col items-center gap-3" aria-label="QR code">
        <div className="rounded-xl border bg-white p-3 shadow-card">
          <QrCode value={url} label={`QR code of the install page of ${installTitle(info)}`} testId="install-qr" className={expired ? 'size-56 opacity-20' : 'size-56'} />
        </div>
        <p className="text-center text-sm text-muted-foreground">Scan it with the phone's camera. No sign-in needed on the phone.</p>
      </section>
      {info.requiresRegisteredDevice ? <RegisteredDevicesNote /> : null}
      <section className="flex flex-col gap-2" aria-label="Send link">
        <Kicker>Send link</Kicker>
        <div className="flex items-center gap-2">
          <Input readOnly value={url} aria-label="Install link" data-testid="install-link" className="min-w-0 font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
          <CopyButton text={url} label="Copy" testId="install-copy-link" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className={expired ? 'text-xs text-failed' : 'text-xs text-subtle'} data-testid="install-expiry">{expiryText(info.expiresAt, now)}</p>
          <Button variant="ghost" size="sm" loading={refreshing} onClick={onNewLink} data-testid="install-new-link">
            {refreshing ? null : <Icon name="refresh" size={16} />}
            New link
          </Button>
        </div>
      </section>
      <section className="flex flex-col gap-2" aria-label="On the phone">
        <Kicker>On the phone</Kicker>
        <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
          {installSteps(info).map((s) => <li key={s}>{s}</li>)}
        </ol>
      </section>
    </>
  );
}

function RegisteredDevicesNote() {
  return (
    <Alert data-testid="install-registered-devices">
      <AlertTitle className="col-start-1 col-end-3 flex items-center gap-2">
        <Icon name="phone_iphone" size={18} />
        Registered devices only
      </AlertTitle>
      <AlertDescription className="col-start-1 col-end-3">
        This iOS build installs on the iPhones and iPads that were registered before it was built.{' '}
        <Link to="/devices" className="font-medium text-foreground underline underline-offset-2">Register a device</Link>, then build again to include it.
      </AlertDescription>
    </Alert>
  );
}

function NotInstallable({ info }: { info: ArtifactInstall }) {
  return (
    <EmptyState
      compact
      icon={info.platform === 'ios' ? 'phone_iphone' : 'android'}
      title="This build cannot be installed on a phone"
      description={info.message ?? undefined}
      testId="install-not-installable"
      action={pointsToDevices(info) ? <Button asChild variant="outline"><Link to="/devices">iOS devices</Link></Button> : undefined}
    />
  );
}

/** The geometry of the ready state: the QR tile, the link row and three steps. */
function InstallSkeleton() {
  return (
    <div className="flex flex-col items-center gap-5" aria-busy="true" data-testid="install-loading">
      <Skeleton className="size-[248px] rounded-xl" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-9 w-full" />
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-3 w-4/5" />
        <Skeleton className="h-3 w-3/5" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}
